<script setup lang="ts">
import { onClickOutside } from "@vueuse/core";
import { Loader2Icon } from "lucide-vue-next";
import { computed, type Ref, ref } from "vue";
import { label } from "../../mixins/translations";
import { useSearchStore } from "../../stores/search";

interface Props {
    types?: string[];
}

const props = withDefaults<Props>(defineProps<Props>(), {
    types: [],
});

const searchStore = useSearchStore();

const selectedOption: Ref<string> = ref("all");
const searchQuery: Ref<string> = ref("");
const openResults: Ref<boolean> = ref(false);

const searchUrl = computed((): string => {
    let url = "/search/" + searchQuery.value.toLowerCase() + "/";
    if (selectedOption.value !== "all") {
        url = url + selectedOption.value.toLowerCase();
    }
    return url;
});

const navigate = () => {
    if (searchQuery.value.length) {
        window.location.href = searchUrl.value;
    }
};

const preview = async () => {
    await searchStore.getSearchResults(searchQuery.value, selectedOption.value);
    openResults.value = true;
};

const target = ref(null);
onClickOutside(target, (event) => (openResults.value = false));
</script>

<template>
    <div class="w-full relative pt-3 pb-3 mx-auto text-secondary-600 bg-secondary-50 hidden z-20" id="mainMenuSearch">
        <div class="max-w-(--breakpoint-xl) relative mx-auto">
            <div class="relative w-full max-w-xs xl:max-w-lg 2xl:max-w-2xl bg-white flex items-center border border-gray-200 rounded-lg focus-within:shadow-lg mx-auto">
                <select v-if="types" class="outline-hidden border-0 bg-transparent font-medium text-sm p-4 w-[120px] focus:ring-0" v-model="selectedOption">
                    <option value="all">{{ label('site.search.all') }}</option>
                    <option v-for="(key,type) in types" :value="key" v-text="label('site.search.'+key)"></option>
                </select>
                <input
                    class="appearance-none outline-hidden focus:border-secondary-300 border-l border-0 focus:ring-0 border-secondary-300 bg-transparent font-normal text-sm pl-4 w-9/12"
                    type="text" v-model="searchQuery" :placeholder="label('site.search.placeholder')" @keyup.enter="navigate" @keyup="preview">
                <div class="ml-auto px-4 text-muted-foreground">
                    <svg v-if="!searchStore.isLoading" xmlns="http://www.w3.org/2000/svg" class="size-5" fill="none" @click="navigate"
                         viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                    </svg>
                    <Loader2Icon v-else class="size-5 animate-spin" />
                </div>

                <!--SEARCHRESULTS-->
                <div class="w-full absolute top-16 bg-gray-50 shadow-xl rounded-xl" v-show="openResults" ref="target">
                    <div v-for="(results, key) in searchStore.results">
                        <div v-if="results.count">
                            <div v-if="key === 'articles'" class="w-full py-4 px-6 border-t border-gray-200 bg-gray-100">
                                <h3 class="w-full text-lg font-bold tracking-tight text-gray-900" v-text="label('site.search.'+key)"></h3>
                                <div v-for="result in results.items">
                                    <a :href="result.full_slug" class="w-full flex bg-white rounded-xl overflow-hidden mt-3" >
                                        <div class="w-1/4 h-auto bg-cover bg-center" :style="`background-image: url('${result.image}')`">
                                        </div>
                                        <div class="w-3/4 p-4">
                                            <h3 class="w-full text-lg font-bold tracking-tight text-gray-900" v-text="result.title"></h3>
                                            <p class="w-full mt-1 font-light text-sm" v-html="result.content_short"></p>
                                        </div>
                                    </a>
                                </div>
                            </div>
                            <div v-else-if="key === 'products'" class="w-full py-4 px-6 border-t border-gray-200 bg-gray-100">
                                <h3 class="w-full text-lg font-bold tracking-tight text-gray-900" v-text="label('site.search.'+key)"></h3>
                                <div v-for="result in results.items">
                                    <a :href="`/${result.slug}/`" class="w-full flex bg-white rounded-xl overflow-hidden mt-3" >
                                        <div class="w-1/4 h-auto bg-cover bg-center" :style="`background-image: url('${result.thumbnail.image_path}')`">

                                        </div>
                                        <div class="w-3/4 p-4">
                                            <h3 class="w-full text-lg font-bold tracking-tight text-gray-900" v-text="result.name"></h3>
                                        </div>
                                    </a>
                                </div>
                            </div>
        <!--                    <div v-else-if="key === 'tags'" class="w-full flex py-4 px-6 border-t border-gray-200 gap-4">-->
        <!--                        <a href="#" class="px-4 py-2 text-black text-sm font-normal bg-gray-100 hover:bg-white rounded-full focus:bg-primary-500 focus:outline-hidden">#praktijkondersteuners</a>-->
        <!--                        <a href="#" class="px-4 py-2 text-black text-sm font-normal bg-gray-100 hover:bg-white rounded-full focus:bg-primary-500 focus:outline-hidden">#Kennis en wetenschap</a>-->
        <!--                    </div>-->
                            <div v-else class="w-full py-4 px-6">
                                <h3 class="w-full text-lg font-bold tracking-tight text-gray-900" v-text="label('site.search.'+key)"></h3>
                                <nav class="flex flex-col mt-2 space-y-2 text-sm text-gray-500">
                                    <div v-for="result in results.items">
                                        <a class="hover:text-black" :href="result.full_slug" v-text="result.title"></a>
                                    </div>
                                </nav>
                            </div>
                        </div>
                    </div>
                    <div class="w-full py-4 px-6 flex" v-if="searchStore.totalResults">
                        <a :href="searchUrl" class="px-6 py-3 w-full text-white text-center text-base font-bold bg-black hover:bg-primary-500 rounded-full focus:bg-primary-500 focus:outline-hidden" v-html="label('site.search.show-all-results', {'count': searchStore.totalResults})"></a>
                    </div>
                    <div v-else class="w-full py-4 px-6 flex">
                        <div v-text="label('site.search.no-results')"></div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
